import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Settings, GripVertical, Plus, Trash2, Edit, Copy, Code, Palette,
  MessageSquare, Mail, Send, Clock, CheckCircle
} from "lucide-react";

export default function LeadSettings() {
  const [stages, setStages] = useState([]);
  const [formSettings, setFormSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [stageForm, setStageForm] = useState({ name: "", color: "#6366f1" });
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [stagesRes, settingsRes] = await Promise.all([
        api.get("/tenant/lead-pipeline"),
        api.get("/tenant/lead-form-settings")
      ]);
      setStages(stagesRes.data || []);
      setFormSettings(settingsRes.data || {});
    } catch {
      toast.error("Gagal memuat pengaturan");
    } finally {
      setLoading(false);
    }
  };

  // Pipeline Stage Management
  const handleSaveStage = async () => {
    if (!stageForm.name.trim()) { toast.error("Nama stage wajib diisi"); return; }
    try {
      if (editingStage) {
        await api.put(`/tenant/lead-pipeline/${editingStage.id}`, stageForm);
        toast.success("Stage diperbarui");
      } else {
        await api.post("/tenant/lead-pipeline", stageForm);
        toast.success("Stage ditambahkan");
      }
      setShowStageDialog(false);
      setEditingStage(null);
      setStageForm({ name: "", color: "#6366f1" });
      fetchAll();
    } catch {
      toast.error("Gagal menyimpan stage");
    }
  };

  const handleDeleteStage = async (stageId) => {
    try {
      await api.delete(`/tenant/lead-pipeline/${stageId}`);
      toast.success("Stage dihapus");
      fetchAll();
    } catch {
      toast.error("Gagal menghapus stage");
    }
  };

  const openEditStage = (stage) => {
    setEditingStage(stage);
    setStageForm({ name: stage.name, color: stage.color });
    setShowStageDialog(true);
  };

  const openAddStage = () => {
    setEditingStage(null);
    setStageForm({ name: "", color: "#6366f1" });
    setShowStageDialog(true);
  };

  // Move stage up/down in order
  const moveStage = async (index, direction) => {
    const newStages = [...stages];
    const target = index + direction;
    if (target < 0 || target >= newStages.length) return;
    [newStages[index], newStages[target]] = [newStages[target], newStages[index]];
    const reordered = newStages.map((s, i) => ({ id: s.id, order: i }));
    try {
      await api.put("/tenant/lead-pipeline/reorder", reordered);
      fetchAll();
    } catch {
      toast.error("Gagal mengubah urutan");
    }
  };

  // Form Settings
  const handleSaveFormSettings = async () => {
    try {
      await api.put("/tenant/lead-form-settings", formSettings);
      toast.success("Pengaturan form disimpan");
    } catch {
      toast.error("Gagal menyimpan pengaturan");
    }
  };

  // Add custom field
  const addCustomField = () => {
    setFormSettings(prev => ({
      ...prev,
      custom_fields: [...(prev.custom_fields || []), { name: "", label: "", type: "text", required: false }]
    }));
  };

  const updateCustomField = (index, field, value) => {
    setFormSettings(prev => {
      const fields = [...(prev.custom_fields || [])];
      fields[index] = { ...fields[index], [field]: value };
      return { ...prev, custom_fields: fields };
    });
  };

  const removeCustomField = (index) => {
    setFormSettings(prev => ({
      ...prev,
      custom_fields: (prev.custom_fields || []).filter((_, i) => i !== index)
    }));
  };

  const tenantId = formSettings?.tenant_id || "";
  const embedCode = `<iframe src="${window.location.origin}/lead-form/${tenantId}" width="100%" height="600" frameborder="0" style="border: none; border-radius: 8px;"></iframe>`;

  const copyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    toast.success("Kode embed disalin");
  };

  if (loading) return <div className="p-6 text-center text-muted-foreground">Memuat...</div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Settings className="w-6 h-6" /> Pengaturan Lead</h1>
        <p className="text-muted-foreground text-sm">Kelola pipeline, form lead, dan auto follow-up</p>
      </div>

      {/* Pipeline Stages */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Pipeline Stages</CardTitle>
              <CardDescription>Atur tahapan pipeline lead Anda</CardDescription>
            </div>
            <Button size="sm" onClick={openAddStage}><Plus className="w-4 h-4 mr-1" /> Tambah Stage</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stages.map((stage, index) => (
              <div key={stage.id} className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveStage(index, -1)} disabled={index === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs">▲</button>
                  <button onClick={() => moveStage(index, 1)} disabled={index === stages.length - 1}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-xs">▼</button>
                </div>
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                <span className="font-medium flex-1">{stage.name}</span>
                {stage.is_default && <Badge variant="secondary" className="text-xs">Default</Badge>}
                <span className="text-xs text-muted-foreground">#{index + 1}</span>
                <Button size="sm" variant="ghost" onClick={() => openEditStage(stage)}><Edit className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => handleDeleteStage(stage.id)}
                  disabled={stages.length <= 1}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lead Form Settings */}
      {formSettings && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Pengaturan Form Lead</CardTitle>
                <CardDescription>Konfigurasi form publik untuk menerima lead</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowEmbedDialog(true)}>
                  <Code className="w-4 h-4 mr-1" /> Kode Embed
                </Button>
                <Button size="sm" onClick={handleSaveFormSettings}>Simpan</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Form Aktif</p>
                <p className="text-xs text-muted-foreground">Aktifkan/nonaktifkan form publik</p>
              </div>
              <Switch checked={formSettings.enabled} onCheckedChange={v => setFormSettings(p => ({ ...p, enabled: v }))} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Judul Form</label>
                <Input value={formSettings.title || ""} onChange={e => setFormSettings(p => ({ ...p, title: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Pesan Sukses</label>
                <Input value={formSettings.success_message || ""} onChange={e => setFormSettings(p => ({ ...p, success_message: e.target.value }))} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Deskripsi</label>
              <Textarea value={formSettings.description || ""} onChange={e => setFormSettings(p => ({ ...p, description: e.target.value }))} />
            </div>

            {/* Required Fields */}
            <div>
              <label className="text-sm font-medium mb-2 block">Field Wajib</label>
              <div className="flex flex-wrap gap-2">
                {["name", "phone", "email", "product_interest", "budget"].map(field => (
                  <label key={field} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={(formSettings.required_fields || []).includes(field)}
                      onChange={e => {
                        const fields = formSettings.required_fields || [];
                        setFormSettings(p => ({
                          ...p,
                          required_fields: e.target.checked ? [...fields, field] : fields.filter(f => f !== field)
                        }));
                      }} />
                    {field === "name" ? "Nama" : field === "phone" ? "Telepon" : field === "email" ? "Email" : field === "product_interest" ? "Minat Produk" : "Budget"}
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Fields */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Custom Fields</label>
                <Button size="sm" variant="outline" onClick={addCustomField}><Plus className="w-3 h-3 mr-1" /> Tambah Field</Button>
              </div>
              {(formSettings.custom_fields || []).map((cf, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <Input placeholder="Label" value={cf.label} onChange={e => updateCustomField(i, "label", e.target.value)} className="flex-1" />
                  <Input placeholder="Nama field" value={cf.name} onChange={e => updateCustomField(i, "name", e.target.value)} className="flex-1" />
                  <Select value={cf.type || "text"} onValueChange={v => updateCustomField(i, "type", v)}>
                    <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="select">Select</SelectItem>
                      <SelectItem value="textarea">Textarea</SelectItem>
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                    <input type="checkbox" checked={cf.required || false} onChange={e => updateCustomField(i, "required", e.target.checked)} /> Wajib
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => removeCustomField(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auto Follow-Up Settings */}
      {formSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Auto Follow-Up</CardTitle>
            <CardDescription>Kirim pesan otomatis saat lead baru masuk via form</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Auto Follow-Up Aktif</p>
                <p className="text-xs text-muted-foreground">Otomatis kirim pesan saat lead baru masuk</p>
              </div>
              <Switch checked={formSettings.auto_followup_enabled || false}
                onCheckedChange={v => setFormSettings(p => ({ ...p, auto_followup_enabled: v }))} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Channel</label>
                <Select value={formSettings.auto_followup_channel || "whatsapp"}
                  onValueChange={v => setFormSettings(p => ({ ...p, auto_followup_channel: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="both">Keduanya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Delay (menit)</label>
                <Input type="number" min="0" value={formSettings.auto_followup_delay_minutes || 0}
                  onChange={e => setFormSettings(p => ({ ...p, auto_followup_delay_minutes: parseInt(e.target.value) || 0 }))} />
                <p className="text-xs text-muted-foreground mt-1">0 = langsung kirim</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Template Pesan</label>
              <Textarea rows={3} value={formSettings.auto_followup_message || ""}
                onChange={e => setFormSettings(p => ({ ...p, auto_followup_message: e.target.value }))}
                placeholder="Halo {name}, terima kasih telah menghubungi kami..." />
              <p className="text-xs text-muted-foreground mt-1">Variabel: {"{name}"}, {"{phone}"}, {"{email}"}, {"{product_interest}"}</p>
            </div>

            <Button onClick={handleSaveFormSettings}>Simpan Pengaturan</Button>
          </CardContent>
        </Card>
      )}

      {/* Stage Dialog */}
      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStage ? "Edit Stage" : "Tambah Stage"}</DialogTitle>
            <DialogDescription>Konfigurasi tahapan pipeline</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nama Stage" value={stageForm.name} onChange={e => setStageForm(p => ({ ...p, name: e.target.value }))} />
            <div>
              <label className="text-sm font-medium">Warna</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={stageForm.color} onChange={e => setStageForm(p => ({ ...p, color: e.target.value }))} className="w-10 h-10 rounded cursor-pointer" />
                <Input value={stageForm.color} onChange={e => setStageForm(p => ({ ...p, color: e.target.value }))} className="w-28" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageDialog(false)}>Batal</Button>
            <Button onClick={handleSaveStage}>{editingStage ? "Update" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kode Embed Form Lead</DialogTitle>
            <DialogDescription>Salin kode ini dan tempel di website Anda</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-muted p-3 rounded-md">
              <code className="text-xs break-all">{embedCode}</code>
            </div>
            <p className="text-xs text-muted-foreground">
              Atau gunakan link langsung: <br />
              <a href={`${window.location.origin}/lead-form/${tenantId}`} target="_blank" rel="noreferrer"
                className="text-blue-600 underline">{window.location.origin}/lead-form/{tenantId}</a>
            </p>
          </div>
          <DialogFooter>
            <Button onClick={copyEmbed}><Copy className="w-4 h-4 mr-2" /> Salin Kode</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
